// import node module libraries
import React, { useMemo, Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Image, OverlayTrigger, Tooltip, Row, Col, Card, Spinner } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, MoreVertical, Trash, Edit, Mail } from 'react-feather';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// import media files
import Avatar from 'assets/images/avatar/avatar.jpg';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import OffcanvasEditUser from './OffcanvasEditUser';
import ModalDeleteUser from './ModalDeleteUser';

// import custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';

const UsersListItemsSettings = ({ activeKey }) => {
	const { t } = useTranslation();
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(null); // State for loading table

	// Paging start
	const [paginationData, setPaginationData] = useState([]); // State for pagination data
	const usersPerPage = 30;

	// Paging end
	
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// Form Data Users
	const [formDataUser, setFormDataUser] = useState([]); // State for form data
	const [dataUsers, setDataUsers] = useState({ data: [] }); // State for response data
	const [usersList, setUsersList] = useState([]); // State for response data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async (page) => {
		if (!page) {
			page = 1;
		}
		setIsLoading(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Tenant Data
		await axios.get(`${baseURL}/users?limit=${usersPerPage}&page=${page}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setDataUsers({ data: responseData });
				setUsersList(responseData);
				setPaginationData(response.data.pagination);
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	useEffect(() => {
		if (activeKey === 'users') {
			getData();
		}
	}, [activeKey]);

	// Delete User
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [isLoadingButtonUserDelete, setIsLoadingButtonUserDelete] = useState(null); // State for loading spinner
	const [userToDelete, setUserToDelete] = useState(null); // State for user to delete

	const handleDeleteClick = (user) => {
		setUserToDelete(user);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		if (userToDelete) {
			setIsLoadingButtonUserDelete(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/users/${userToDelete.id}`, options)
				.then(response => {
					setUsersList(usersList.filter(user => user.id !== userToDelete.id));
					setShowDeleteModal(false);
					setIsLoadingButtonUserDelete(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Der Benutzer wurde erfolgreich gelöscht');
				})
				.catch((error) => {
					setIsLoadingButtonUserDelete(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	// Edit User

	const [showEditUserOffcanvas, setShowEditUserOffcanvas] = useState(false); // State for showing offcanvas
	const [isLoadingButtonUserEdit, setIsLoadingButtonUserEdit] = useState(null); // State for loading spinner
	const [userToEdit, setUserToEdit] = useState(null); // State for user to edit

	const handleShowEditUser = () => setShowEditUserOffcanvas(true); // Show offcanvas
	const handleCloseEditUser = () => setShowEditUserOffcanvas(false); // Close offcanvas

	const handleEditUser = async (e) => {
		e.preventDefault();

		//setIsButtonDisabled(true);
		setIsLoadingButtonUserEdit(true); // Set loading to true

		const data = {
			user_first_name: e.target.firstname.value,
			user_last_name: e.target.lastname.value,
			user_username: e.target.username.value,
			role_id: userToEdit.role_id,
			user_avatar: null, // not implemented yet
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.patch(`${baseURL}/users/${userToEdit.id}`, data, options)
			.then(response => {
				handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				setIsLoadingButtonUserEdit(false); // Stop loading
				handleCloseEditUser();
				setUsersList(usersList.map(user => user.id === userToEdit.id ? { ...user, ...data, role_id: userToEdit.role_id } : user));
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonUserEdit(false); // Stop loading
			});
	};

	// Send Activation Link to User
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);

	const handleSendActivationLink = async (e) => {
		console.log('ausgelöst');
		setIsButtonDisabled(true);
		//setIsLoading(true); // Set loading to true

		// Get username from data-attribute
		const username = e.currentTarget.getAttribute('data-username');

		// API-Call Options
		const options = {
			params: {
			},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};
		const data = {
			username: username,
			otp_valid_for: "activate_account"
		}

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/auth/generate-otp`, data, options)
			.then(response => {
				setIsButtonDisabled(false);
				//setIsLoading(false); // Stop loading
				handleShowNotification(setNotification, 'info', 'Information', response.data.message + '. Eine Aktivierungs E-Mail wurde an den Benutzer übermittelt.');

			})
			.catch((error) => {
				setIsButtonDisabled(false);
				//setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Es konnte keine Aktivierungs E-Mail zum Benutzer gesendet werden. Bitte versuche es später erneut oder kontaktiere den Support über hello@haeppy-shopping.com.');
			})
	};

	const ActionMenu = ({ row }) => {
		return (
			<Dropdown style={{ zIndex: 1 }}>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('SETTINGS')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => {
						setUserToEdit(row.original);
						handleShowEditUser();
					}}>
						{' '}
						<Edit size="15px" className="dropdown-item-icon" /> {t('Edit')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleDeleteClick(row.original)}>
						{' '}
						<Trash size="15px" className="dropdown-item-icon" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);

	};

	const columns = useMemo(
		() => [
			{
				accessorKey: 'user_avatar',
				header: t('Name'),
				cell: ({ getValue, row }) => {
					return (
						<div className="d-flex align-items-center">
							<Image
								src={row.original.user_avatar || Avatar}
								alt=""
								className="rounded-circle avatar-md me-2"
							/>
							<h5 className="mb-0">{row.original.user_first_name} {row.original.user_last_name}</h5>
						</div>
					);
				}
			},
			{ accessorKey: 'user_username', header: t('Username') },
			{ accessorKey: 'role_id', header: t('Company Administrator') },
			{
				accessorKey: 'user_is_active',
				header: 'Status',
				cell: () => {
					return (
						<div className="align-middle border-top-0">
							<OverlayTrigger
								key="top"
								placement="top"
								overlay={<Tooltip id={`tooltip-top`}>{t('Send activation link')}</Tooltip>}
							>
								<Link href="#">
									<Mail size="15px" className="dropdown-item-icon" />
								</Link>
							</OverlayTrigger>
						</div>
					);
				}
			},
			{
				accessorKey: 'shortcutmenu',
				header: t('Action'),
				cell: () => {
					return <ActionMenu />;
				}
			}
		],
		[]
	);

	const data = useMemo(() => dataUsers.data, []);

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			{/* UsersListItems settings */}
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between">
					<h4 className="mb-0">{t('User Administration')}</h4>
				</Card.Header>
				<Card.Body id="plans-settings">
					{isLoading ? (
						<div className="d-flex justify-content-center">
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</div>
					) : (
						<span>{t('This Area is currently in development and will be available very soon')}!</span>
					)}
				</Card.Body>
			</Card>
			{/*<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Benutzer suchen"
				pagination={true} 
			/>*/}
		</Fragment>
	);
};

export default UsersListItemsSettings;
